import { Copy01Icon } from '@hugeicons/core-free-icons';
import type { PromoWithReceipts } from '@sunrise-ui/api/events';
import {
  Colors,
  COMMUNITY_APP_URL,
  IconButton,
  StatusPill,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableScrollContainer,
  TableSkeletonLoader,
  TextReg,
  useDynamicStyles,
  useSnackbar,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import * as Clipboard from 'expo-clipboard';
import React, { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';

type Props = {
  promos: PromoWithReceipts[];
  isLoading?: boolean;
  communitySlug?: string;
  eventId?: string;
};

const getEventUrl = (
  communitySlug: string,
  promoCode: string,
  eventId: string,
  ticketTypeId?: string,
): string => {
  const params = new URLSearchParams();
  params.set('promoCode', promoCode);
  params.set('eventId', eventId);
  if (ticketTypeId) {
    params.set('ticketTypeId', ticketTypeId);
  }
  return `${COMMUNITY_APP_URL}/events/${communitySlug}?${params.toString()}`;
};

const isPromoRegistered = (promo: PromoWithReceipts): boolean => {
  const receipt = promo.receipts?.[0];
  return receipt?.user_id != null;
};

export function GroupPurchaseTable({
  promos,
  isLoading = false,
  communitySlug,
  eventId,
}: Props) {
  const styles = useDynamicStyles(createStyles);
  const { showSnackbar } = useSnackbar();

  const handleCopyRegistrationLink = useCallback(
    async (promo: PromoWithReceipts) => {
      if (!communitySlug || !eventId) {
        showSnackbar({
          text: 'Unable to copy link - event information not loaded',
          type: 'error',
        });
        return;
      }

      try {
        const { discount_code, ticket_types } = promo;
        const ticketTypeId = ticket_types?.[0]?.id;
        const url = getEventUrl(
          communitySlug,
          discount_code,
          eventId,
          ticketTypeId,
        );
        await Clipboard.setStringAsync(url);
        showSnackbar({
          text: 'Unique registration link copied!',
          type: 'success',
        });
      } catch {
        showSnackbar({
          text: 'Failed to copy link',
          type: 'error',
        });
      }
    },
    [showSnackbar, communitySlug, eventId],
  );

  const renderStatusBadge = (promo: PromoWithReceipts) => {
    const registered = isPromoRegistered(promo);
    return (
      <StatusPill
        text={registered ? 'Registered' : 'Not Registered'}
        backgroundColor={
          registered ? Colors.green0 + '40' : Colors.orange0 + '40'
        }
      />
    );
  };

  const renderRow = (promo: PromoWithReceipts) => {
    const receipt = promo.receipts?.[0];
    const fullName =
      `${receipt?.first_name ?? ''} ${receipt?.last_name ?? ''}`.trim();
    // Join ticket type names with commas, or show em-dash if none exist
    const ticketTypeName =
      promo.ticket_types?.map((t) => t.name).join(', ') || '—';

    return (
      <TableRow key={promo.id}>
        <TableCell flex={2}>
          <TextReg style={styles.cellText} numberOfLines={1}>
            {fullName || '—'}
          </TextReg>
        </TableCell>
        <TableCell flex={2}>
          <TextReg style={styles.cellText} numberOfLines={1}>
            {receipt?.email || '—'}
          </TextReg>
        </TableCell>
        <TableCell flex={1.5}>
          <View style={styles.inlineCopyContainer}>
            <TextReg style={styles.cellText} numberOfLines={1}>
              {promo.discount_code || '—'}
            </TextReg>
            <IconButton
              icon={Copy01Icon}
              size="small"
              onPress={() => {
                void handleCopyRegistrationLink(promo);
              }}
            />
          </View>
        </TableCell>
        <TableCell flex={1.5}>
          <TextReg style={styles.cellText} numberOfLines={2}>
            {ticketTypeName}
          </TextReg>
        </TableCell>
        <TableCell flex={1} align="center">
          {renderStatusBadge(promo)}
        </TableCell>
      </TableRow>
    );
  };

  const loadingContent = (
    <TableRow>
      <TableCell flex={2}>
        <TableSkeletonLoader style={styles.skeletonBox} />
      </TableCell>
      <TableCell flex={2}>
        <TableSkeletonLoader style={styles.skeletonBox} />
      </TableCell>
      <TableCell flex={1.5}>
        <TableSkeletonLoader style={styles.skeletonBox} />
      </TableCell>
      <TableCell flex={1.5}>
        <TableSkeletonLoader style={styles.skeletonBox} />
      </TableCell>
      <TableCell flex={1} align="center">
        <TableSkeletonLoader style={styles.skeletonBadge} />
      </TableCell>
    </TableRow>
  );

  return (
    <TableScrollContainer>
      <Table>
        <TableHeader>
          <TableHead flex={2}>Attendee</TableHead>
          <TableHead flex={2}>Email</TableHead>
          <TableHead flex={1.5}>Discount Code</TableHead>
          <TableHead flex={1.5}>Ticket Type</TableHead>
          <TableHead flex={1} align="center">
            Status
          </TableHead>
        </TableHeader>
        <TableBody
          isLoading={isLoading}
          loadingRows={3}
          loadingContent={loadingContent}
          isEmpty={promos.length === 0}
          emptyContent={
            <View style={styles.emptyContainer}>
              <TextReg style={styles.emptyStateText}>No tickets found</TextReg>
            </View>
          }
        >
          {promos.map(renderRow)}
        </TableBody>
      </Table>
    </TableScrollContainer>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    inlineCopyContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      flex: 1,
      minWidth: 0,
    },
    cellText: {
      color: Colors.text,
      flex: 1,
    },
    emptyContainer: {
      padding: 32,
      alignItems: 'center',
    },
    emptyStateText: {
      fontSize: isMobile ? 14 : 16,
      color: Colors.text,
      opacity: 0.6,
      textAlign: 'center',
    },
    skeletonBox: {
      height: 16,
      backgroundColor: Colors.opacity10,
      borderRadius: 4,
      width: '80%',
    },
    skeletonBadge: {
      height: 24,
      width: 70,
      backgroundColor: Colors.opacity10,
      borderRadius: 12,
    },
  });
